'use client'

import {useQuery} from 'react-query'
import {getRequest, postRequest} from '../../../setup/redux/common/cruds'
import type {AxiosResponse} from 'axios'
import type {PackagesQueryResponse} from '../../modules/packages/packages-list/core/_models'
import {Col, Row, Table} from 'react-bootstrap'
import React, {useEffect, useState} from 'react'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {GetPNRNumber, LongTimeFormat, makeFirstCharToUpperCase} from '../../modules/bookings/helper'
import moment from 'moment'
import {
  DropToWithoutAddress,
  ManageVehicle,
  PickupFromWithoutAddress,
} from '../../modules/bookings/pages/transferPage/helpers/TransferHelper'
import {manageVehicle} from '../../modules/bookings/pages/sightSeenPage/booking-voucher/Private'
import Pagination from '@mui/material/Pagination'
import {ManageSightSeenSupplier} from './partial/ManageSightSeenSupplier'
import {ManageTransferSupplier} from './partial/ManageTransferSupplier'
import {ManageCustomServiceSupplier} from './partial/ManageCustomServiceSupplier'
import {ManageTransferRemark} from './partial/ManageTransferRemark'
import {ManageSightSeenRemark} from './partial/ManageSightSeenRemark'
import {ManageCustomServiceRemark} from './partial/ManageCustomServiceRemark'
import {ManagePackageItinerarySupplier} from './partial/ManagePackageItinerarySupplier'
import {ManagePackageItineraryRemark} from './partial/ManagePackageItineraryRemark'
import {UpdateConfirmationNumber} from '../../modules/bookings/pages/transferPage/helpers/UpdateConfirmationNumber'
import {Link} from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  copyToClipboardAsync,
  handleNotificationWithToastify,
  useCheckAccess,
} from '../../services/Functional'
import {formatPhone} from '../../utils/formatPhone'
import {
  StatusCounts,
  parseStatusCounts,
  SERVICE_BOOKING_STATUS_OPTIONS,
} from './partial/statusCounts'
import {OnTourStatusSelect} from './partial/OnTourStatusSelect'
import {useDispatch} from 'react-redux'
import {getCurrencyDropdown} from '../../../setup/redux/suppliers/actions'

const DEFAULT_DATE = moment().add(1, 'day').format('YYYY-MM-DD')

export const ManageBookingServices = (props: {
  country: string
  countryCode: string
  onStatusCounts?: (counts: StatusCounts | undefined) => void
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)

  // Pending filter state (bound to inputs, applied on Search click)
  const [searchName, setSearchName] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [serviceDate, setServiceDate] = useState(DEFAULT_DATE)
  const [isSearchApplied, setIsSearchApplied] = useState(false)

  // Applied filter state (drives the query)
  const [appliedSearchName, setAppliedSearchName] = useState('')
  const [appliedDateFrom, setAppliedDateFrom] = useState('')
  const [appliedDateTo, setAppliedDateTo] = useState('')
  const [appliedServiceDate, setAppliedServiceDate] = useState('')

  const [bookingStatus, setBookingStatus] = useState('')
  const [appliedBookingStatus, setAppliedBookingStatus] = useState('')

  const checkAccess = useCheckAccess()
  const scope = checkAccess('dashboard.show_all') ? 'all' : 'own'

  const activeDate = appliedServiceDate || DEFAULT_DATE
  const buildUrl = () => {
    let url = `${process.env.REACT_APP_API_BASE_URL}dashboard/services/${activeDate}?page=${currentPage}&items_per_page=10&country=${props.countryCode}&scope=${scope}`
    if (appliedSearchName) url += `&search_name=${encodeURIComponent(appliedSearchName)}`
    if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`
    if (appliedDateTo) url += `&date_to=${appliedDateTo}`
    if (appliedBookingStatus) url += `&booking_status=${appliedBookingStatus}`
    return url
  }

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `DASHBOARD-MANAGE-SERVICES-${props.countryCode}-${activeDate}-${appliedSearchName}-${appliedDateFrom}-${appliedDateTo}-${appliedBookingStatus}-${currentPage}`,
    () => getRequest(buildUrl()).then((d: AxiosResponse<PackagesQueryResponse>) => d.data),
    {
      cacheTime: 5000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchInterval: 60000,
    }
  )
  const handlePaginationChange = async (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page)
  }
  const handleSearch = () => {
    setAppliedSearchName(searchName)
    setAppliedDateFrom(dateFrom)
    setAppliedDateTo(dateTo)
    setAppliedServiceDate(serviceDate)
    setAppliedBookingStatus(bookingStatus)
    setIsSearchApplied(true)
    setCurrentPage(1)
  }
  const handleClear = () => {
    setSearchName('')
    setDateFrom('')
    setDateTo('')
    setServiceDate(DEFAULT_DATE)
    setAppliedSearchName('')
    setAppliedDateFrom('')
    setAppliedDateTo('')
    setAppliedServiceDate(DEFAULT_DATE)
    setBookingStatus('')
    setAppliedBookingStatus('')
    setIsSearchApplied(false)
    setCurrentPage(1)
  }

  useEffect(() => {
    if (isSearchApplied) return
    const timer = setTimeout(() => {
      setAppliedServiceDate(serviceDate)
    }, 600)
    return () => clearTimeout(timer)
  }, [serviceDate])

  useEffect(() => {
    refetch()
  }, [currentPage])

  // Loaded once here rather than in each OnTour row, which would fire the same request
  // once per service on screen.
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getCurrencyDropdown())
  }, [])

  // The filters live here but the badges render in the accordion header, which the wrapper owns.
  const onStatusCounts = props.onStatusCounts
  useEffect(() => {
    onStatusCounts?.(parseStatusCounts(response?.data?.status_counts))
  }, [response, onStatusCounts])

  const removeTransferSupplier = (transferId: number, supplierId: number) => {
    Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        postRequest(`${process.env.REACT_APP_API_BASE_URL}bookings/remove-transfer-supplier`, {
          transfer_id: transferId,
          supplier_id: supplierId,
        })
          .then((response) => {
            refetch()
          })
          .catch((e) => {
            console.log(e)
          })
      }
    })
  }

  const removeSightSeeingSupplier = (sightseeingId: number, supplierId: number) => {
    Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        postRequest(`${process.env.REACT_APP_API_BASE_URL}bookings/remove-sightseeing-supplier`, {
          sightseeing_id: sightseeingId,
          supplier_id: supplierId,
        })
          .then((response) => {
            refetch()
          })
          .catch((e) => {
            console.log(e)
          })
      }
    })
  }

  const removeCustomServiceSupplier = (customServiceId: number, supplierId: number) => {
    Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        postRequest(
          `${process.env.REACT_APP_API_BASE_URL}bookings/remove-custom-service-supplier`,
          {
            custom_service_id: customServiceId,
            supplier_id: supplierId,
          }
        )
          .then((response) => {
            refetch()
          })
          .catch((e) => {
            console.log(e)
          })
      }
    })
  }

  const removePackageItinerarySupplier = (itineraryId: number, supplierId: number) => {
    Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        postRequest(
          `${process.env.REACT_APP_API_BASE_URL}bookings/remove-package-itinerary-supplier`,
          {
            itinerary_id: itineraryId,
            supplier_id: supplierId,
          }
        )
          .then((response) => {
            refetch()
          })
          .catch((e) => {
            console.log(e)
          })
      }
    })
  }

  const [showTransferSupplierModal, setShowTransferSupplierModal] = useState(false)
  const [showSightSeeingSupplierModal, setShowSightSeeingSupplierModal] = useState(false)
  const [showCustomServiceSupplierModal, setShowCustomServiceSupplierModal] = useState(false)
  const [showTransferDateChangeModal, setShowTransferDateChangeModal] = useState(false)
  const [showSightSeeingDateChangeModal, setShowSightSeeingDateChangeModal] = useState(false)
  const [showCustomServiceDateChangeModal, setShowCustomServiceDateChangeModal] = useState(false)
  const [showPackageItinerarySupplierModal, setShowPackageItinerarySupplierModal] = useState(false)
  const [showPackageItineraryRemarkModal, setShowPackageItineraryRemarkModal] = useState(false)
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] = useState(false)
  const [activeService, setActiveService] = useState()
  const [editingSupplier, setEditingSupplier] = useState<any>()
  const [confirmationService, setConfirmationService] = useState<{
    id: number
    name: string
    confirmation_no: string
    service: string
  } | null>(null)

  // Helper function to get status badge class
  // An itinerary row is only ever 'processed' or 'confirmed' — vouchering happens
  // on the parent package, which is what the guest is actually issued. Once the
  // package is vouchered every service under it has been sold, so report that.
  const itineraryStatus = (packageItem: any, itineraryItem: any) =>
    packageItem?.booking_status === 'voucher' ? 'voucher' : itineraryItem.booking_status

  const getStatusBadgeClass = (status: string) => {
    if (!status) return 'secondary'
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'cancelled':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  return (
    <>
      <Row className='align-items-end mb-4 g-2'>
        <Col md={3}>
          <label className='form-label fw-semibold small text-muted mb-1'>Search Name</label>
          <input
            className='form-control'
            type='text'
            placeholder='Search name...'
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Date Range From</label>
          <input
            className='form-control'
            type='date'
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Date Range To</label>
          <input
            className='form-control'
            type='date'
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <label className='form-label fw-semibold small text-muted mb-1'>Booking Status</label>
          <select
            className='form-select'
            value={bookingStatus}
            onChange={(e) => setBookingStatus(e.target.value)}
          >
            {SERVICE_BOOKING_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Col>
        <Col md={2} className='border-start ps-3'>
          <label className='form-label fw-semibold small text-muted mb-1'>Service Date</label>
          <input
            className='form-control'
            type='date'
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            disabled={isSearchApplied}
          />
        </Col>
        <Col md='auto' className='d-flex gap-2'>
          <button className='btn btn-primary' onClick={handleSearch}>
            Search
          </button>
          <button className='btn btn-secondary' onClick={handleClear}>
            Clear
          </button>
        </Col>
      </Row>
      <KTCard className='gutter-b shadow-sm'>
        <KTCardBody className='p-0'>
          <Row>
            {response?.data?.services?.map((booking: any, index: number) => (
              <React.Fragment key={`booking_${index}`}>
                {booking?.booking_transfers.map((transferItem: any, transferIndex: number) => (
                  <Col
                    md={12}
                    key={`service_${index}_${transferIndex}_${transferItem.id}`}
                    className='mb-4'
                  >
                    <Table
                      bordered
                      responsive
                      key={`transfer_${transferIndex}`}
                      className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                    >
                      <thead>
                        <tr className='vertical-align-middle'>
                          <th className='text-capitalize gap-2 align-items-center'>
                            <span className='badge badge-primary me-2'>Transfer</span>
                            <span className='fw-bold'>{transferItem.transfer?.transfer_type}</span>
                            {transferItem.transfer_type && (
                              <span className='badge badge-primary ms-2'>
                                {transferItem.transfer?.is_ferry
                                  ? transferItem.transfer_type === 'ticket'
                                    ? 'Ferry Ticket Only'
                                    : transferItem.transfer_type === 'sic'
                                    ? 'Ferry with Transfer (SIC)'
                                    : transferItem.transfer_type === 'private'
                                    ? 'Ferry with Transfer (Private)'
                                    : transferItem.transfer_type
                                  : transferItem.transfer_type}
                              </span>
                            )}
                            <span
                              className={`badge badge-${getStatusBadgeClass(
                                transferItem.booking_status
                              )} ms-2`}
                            >
                              {transferItem.booking_status}
                            </span>
                          </th>
                          <th className='text-center'>
                            <i className='fa fa-calendar me-1'></i>
                            {moment(transferItem.transfer_date).format('DD/MM/YYYY')}
                          </th>
                          <th>
                            <div className='d-flex justify-content-between vertical-align-middle'>
                              <Link
                                target='_blank'
                                to={`/bookings/${booking.identifier}/show`}
                                className='btn btn-sm btn-outline-primary'
                              >
                                <i className='fa fa-external-link me-1'></i>
                                {GetPNRNumber({booking: booking})}
                              </Link>
                              <button
                                className='btn btn-sm btn-success'
                                onClick={() => {
                                  if (transferItem.transfer_suppliers.length > 0) {
                                    copyToClipboardAsync(async () => {
                                      const response = await postRequest(
                                        `${process.env.REACT_APP_API_BASE_URL}bookings/process-transfer`,
                                        {transfer_id: transferItem.id}
                                      )
                                      handleNotificationWithToastify(response.data)
                                      return response.data.data
                                    })
                                  } else {
                                    handleNotificationWithToastify({
                                      message: 'Please select supplier first',
                                      type: 'warning',
                                    })
                                  }
                                }}
                              >
                                <i className='fa fa-check-circle me-1'></i> Process
                              </button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className='text-capitalize w-50'>
                            <i className='fa fa-user me-1 text-primary'></i>
                            {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                          </td>
                          <td>
                            <span className='badge badge-primary me-1'>
                              <i className='fa fa-user me-1'></i>
                              {transferItem.adults.length} Adult
                            </span>
                          </td>
                          <td>
                            {transferItem.children.filter((child: {age: number}) => child.age > 2)
                              .length > 0 ? (
                              <span className='badge badge-info me-1'>
                                <i className='fa fa-child me-1'></i>
                                {
                                  transferItem.children.filter(
                                    (child: {age: number}) => child.age > 2
                                  ).length
                                }{' '}
                                Child
                              </span>
                            ) : (
                              ''
                            )}
                            {transferItem.children.filter((child: {age: number}) => child.age <= 2)
                              .length > 0 ? (
                              <span className='badge badge-secondary'>
                                <i className='fa fa-baby me-1'></i>
                                {
                                  transferItem.children.filter(
                                    (child: {age: number}) => child.age <= 2
                                  ).length
                                }{' '}
                                Infant
                              </span>
                            ) : (
                              ''
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <div className='mb-2'>
                              <span className='fw-bold me-1'>Pickup:</span>
                              <i className='fa fa-map-marker me-1 text-danger'></i>
                              <PickupFromWithoutAddress transfer={transferItem} />
                            </div>
                            <div>
                              <span className='fw-bold me-1'>Drop:</span>
                              <i className='fa fa-map-marker me-1 text-success'></i>
                              <DropToWithoutAddress transfer={transferItem} />
                            </div>
                          </td>
                          <td>
                            {(() => {
                              if (
                                transferItem?.transfer_type?.toLowerCase() === 'private' ||
                                transferItem?.transfer_type?.toLowerCase() === 'semi_private'
                              ) {
                                const responseData = manageVehicle(transferItem.combination)
                                return responseData.map((value: any, idx: number) => (
                                  <span
                                    key={idx}
                                    className='badge badge-primary me-1 text-capitalize'
                                  >
                                    <i className='fa fa-car me-1'></i>
                                    {value[1]} × {value[0]}
                                  </span>
                                ))
                              }
                              return null
                            })()}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <i className='fa fa-clock-o me-1 text-warning'></i>
                            ET{transferItem.transfer?.transfer_type.charAt(0)}:{' '}
                            <LongTimeFormat time={transferItem.estimated_time} />
                          </td>
                          <td>
                            {transferItem.transfer?.transfer_type !== 'Local' && (
                              <span>
                                <i className='fa fa-plane me-1 text-info'></i>
                                Flight No. {transferItem.flight_no}
                              </span>
                            )}
                          </td>
                          <td>
                            <i className='fa fa-clock-o me-1 text-success'></i>
                            Pickup: <LongTimeFormat time={transferItem?.pickup_time} />
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>
                            <button
                              className='btn btn-link p-0 text-decoration-none'
                              onClick={() => {
                                setActiveService(transferItem)
                                setShowTransferDateChangeModal(true)
                              }}
                            >
                              <i className='fa fa-edit me-1'></i> Remarks:
                            </button>
                            <span className='ms-2'>
                              {transferItem.special_request || 'No remarks'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <div className='d-flex justify-content-between flex-start'>
                              <div>
                                <button
                                  className='btn btn-link p-0 text-decoration-none mb-2'
                                  onClick={() => {
                                    setActiveService(transferItem)
                                    setEditingSupplier(undefined)
                                    setShowTransferSupplierModal(true)
                                  }}
                                >
                                  <i className='fa fa-plus-circle me-1'></i> Supplier:
                                </button>
                                {transferItem.transfer_suppliers.map(
                                  (transferSupplier: any, index: number) => (
                                    <div
                                      key={`transfer_supplier_${index}_${transferSupplier.id}`}
                                      className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                                    >
                                      <div className='flex-grow-1'>
                                        <div className='fw-bold'>
                                          {transferSupplier.company_name}
                                        </div>
                                        <div className='small'>
                                          <i className='fa fa-user me-1'></i>{' '}
                                          {transferSupplier.contact_person}
                                          <i className='fa fa-phone ms-2 me-1'></i>{' '}
                                          {formatPhone(transferSupplier.phone)}
                                        </div>
                                        {(transferSupplier.purchase_currency ||
                                          transferSupplier.purchase_price) && (
                                          <div className='small text-muted mt-1'>
                                            <i className='fa fa-money-bill me-1'></i>
                                            {transferSupplier.purchase_currency && (
                                              <span>{transferSupplier.purchase_currency} </span>
                                            )}
                                            {transferSupplier.purchase_price && (
                                              <span>{transferSupplier.purchase_price}</span>
                                            )}
                                            {transferSupplier.purchase_description && (
                                              <span>
                                                {' '}
                                                - {transferSupplier.purchase_description}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        className='btn btn-sm btn-outline-secondary ms-2'
                                        onClick={() => {
                                          setActiveService(transferItem)
                                          setEditingSupplier(transferSupplier)
                                          setShowTransferSupplierModal(true)
                                        }}
                                      >
                                        <i className='fa fa-edit'></i>
                                      </button>
                                      <button
                                        className='btn btn-sm btn-outline-secondary ms-2'
                                        onClick={() =>
                                          removeTransferSupplier(
                                            transferItem.id,
                                            transferSupplier.id
                                          )
                                        }
                                      >
                                        <i className='fa fa-trash text-danger'></i>
                                      </button>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              <span className='badge badge-light text-dark p-2 border'>
                                <i className='fa fa-check-circle me-1 text-success'></i>
                                Confirmation No: {transferItem.confirmation_no || 'Not confirmed'}
                              </span>
                              <button
                                className='btn btn-sm btn-light ms-2'
                                onClick={() => {
                                  setConfirmationService({
                                    id: transferItem.id,
                                    name: transferItem.transfer?.transfer_type,
                                    confirmation_no: transferItem.confirmation_no || '',
                                    service: 'transfer',
                                  })
                                  setShowUpdateConfirmationModal(true)
                                }}
                                title='Edit Confirmation Number'
                              >
                                <i className='fa fa-edit text-primary'></i>
                              </button>
                            </div>
                            <div className='mt-2'>
                              <OnTourStatusSelect
                                service='transfer'
                                id={transferItem.id}
                                status={transferItem.ontour_status}
                                chargeAmount={transferItem.ontour_charge_amount}
                                chargeCurrency={transferItem.ontour_charge_currency}
                                onSaved={refetch}
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                ))}
                {booking?.booking_sight_seeings.map(
                  (sightseeingItem: any, sightseeingIndex: number) => (
                    <Col md={12} key={`sightseeing_${index}_${sightseeingIndex}`} className='mb-4'>
                      <Table
                        bordered
                        responsive
                        key={`sightseeing_data_${sightseeingIndex}`}
                        className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                      >
                        <thead>
                          <tr className='vertical-align-middle'>
                            <th className='text-capitalize gap-2 align-items-center'>
                              <span className='badge badge-success me-2'>Tour</span>
                              <span className='fw-bold'>{sightseeingItem.sight_seeing?.name}</span>
                              {sightseeingItem.type && (
                                <span className='badge badge-primary ms-2'>
                                  {sightseeingItem.type}
                                </span>
                              )}
                              <span
                                className={`badge badge-${getStatusBadgeClass(
                                  sightseeingItem.booking_status
                                )} ms-2`}
                              >
                                {sightseeingItem.booking_status}
                              </span>
                            </th>
                            <th className='text-center'>
                              <i className='fa fa-calendar me-1'></i>
                              {moment(sightseeingItem.pickup_date).format('DD/MM/YYYY')}
                            </th>
                            <th>
                              <div className='d-flex justify-content-between vertical-align-middle'>
                                <Link
                                  target='_blank'
                                  to={`/bookings/${booking.identifier}/show`}
                                  className='btn btn-sm btn-outline-primary'
                                >
                                  <i className='fa fa-external-link me-1'></i>
                                  {GetPNRNumber({booking: booking})}
                                </Link>
                                <button
                                  className='btn btn-sm btn-success'
                                  onClick={() => {
                                    if (sightseeingItem.sight_seeing_suppliers.length > 0) {
                                      copyToClipboardAsync(async () => {
                                        const response = await postRequest(
                                          `${process.env.REACT_APP_API_BASE_URL}bookings/process-sightseeing`,
                                          {sightseeing_id: sightseeingItem.id}
                                        )
                                        handleNotificationWithToastify(response.data)
                                        return response.data.data
                                      })
                                    } else {
                                      handleNotificationWithToastify({
                                        message: 'Please select supplier first',
                                        type: 'warning',
                                      })
                                    }
                                  }}
                                >
                                  <i className='fa fa-check-circle me-1'></i> Process
                                </button>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className='text-capitalize w-50'>
                              <i className='fa fa-user me-1 text-primary'></i>
                              {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                            </td>
                            <td>
                              <span className='badge badge-primary me-1'>
                                <i className='fa fa-user me-1'></i>
                                {sightseeingItem.adults.length} Adult
                              </span>
                            </td>
                            <td>
                              {sightseeingItem.children.filter(
                                (child: {age: number}) => child.age > 2
                              ).length > 0 ? (
                                <span className='badge badge-info me-1'>
                                  <i className='fa fa-child me-1'></i>
                                  {
                                    sightseeingItem.children.filter(
                                      (child: {age: number}) => child.age > 2
                                    ).length
                                  }{' '}
                                  Child
                                </span>
                              ) : (
                                ''
                              )}
                              {sightseeingItem.children.filter(
                                (child: {age: number}) => child.age <= 2
                              ).length > 0 ? (
                                <span className='badge badge-secondary'>
                                  <i className='fa fa-baby me-1'></i>
                                  {
                                    sightseeingItem.children.filter(
                                      (child: {age: number}) => child.age <= 2
                                    ).length
                                  }{' '}
                                  Infant
                                </span>
                              ) : (
                                ''
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <div className='mb-2'>
                                <span className='fw-bold me-1'>Pickup:</span>
                                <i className='fa fa-map-marker me-1 text-danger'></i>
                                {sightseeingItem?.hotel?.name}
                              </div>
                            </td>
                            <td>
                              {(() => {
                                const type = sightseeingItem.type?.toLowerCase()
                                if (type === 'sic') {
                                  return (
                                    <span className='badge badge-primary'>
                                      <i className='fa fa-car me-1'></i>
                                      {sightseeingItem?.sight_seeing?.parameters
                                        ?.sic_vehicle_type || 'SIC'}
                                    </span>
                                  )
                                } else if (type === 'ticket_only' || type === 'ticket only') {
                                  return (
                                    <span className='badge badge-primary'>
                                      <i className='fa fa-ticket me-1'></i>
                                      Ticket Only
                                    </span>
                                  )
                                } else {
                                  const responseData = ManageVehicle(sightseeingItem.combination)
                                  return responseData.map((value: any, idx: number) => (
                                    <span key={idx} className='badge badge-primary me-1'>
                                      <i className='fa fa-car me-1'></i>
                                      {type === 'semi_private' ? 'Semi Private' : 'Private'}{' '}
                                      {value[1]} {makeFirstCharToUpperCase(value[0])}
                                    </span>
                                  ))
                                }
                              })()}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <i className='fa fa-clock-o me-1 text-warning'></i>
                              Pickup: <LongTimeFormat time={sightseeingItem?.pickup_time} />
                            </td>
                            <td colSpan={2}>
                              <i className='fa fa-clock-o me-1 text-success'></i>
                              Start: <LongTimeFormat time={sightseeingItem?.start_time} />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3}>
                              <button
                                className='btn btn-link p-0 text-decoration-none'
                                onClick={() => {
                                  setActiveService(sightseeingItem)
                                  setShowSightSeeingDateChangeModal(true)
                                }}
                              >
                                <i className='fa fa-edit me-1'></i> Remarks:
                              </button>
                              <span className='ms-2'>
                                {sightseeingItem.special_request || 'No remarks'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <div className='d-flex justify-content-between flex-start'>
                                <div>
                                  <button
                                    className='btn btn-link p-0 text-decoration-none mb-2'
                                    onClick={() => {
                                      setActiveService(sightseeingItem)
                                      setEditingSupplier(undefined)
                                      setShowSightSeeingSupplierModal(true)
                                    }}
                                  >
                                    <i className='fa fa-plus-circle me-1'></i> Supplier:
                                  </button>
                                  {sightseeingItem.sight_seeing_suppliers.map(
                                    (sightseeingSupplier: any, index: number) => (
                                      <div
                                        key={`sightseeing_supplier_${index}_${sightseeingSupplier.id}`}
                                        className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                                      >
                                        <div className='flex-grow-1'>
                                          <div className='fw-bold'>
                                            {sightseeingSupplier.company_name}
                                          </div>
                                          <div className='small'>
                                            <i className='fa fa-user me-1'></i>{' '}
                                            {sightseeingSupplier.contact_person}
                                            <i className='fa fa-phone ms-2 me-1'></i>{' '}
                                            {formatPhone(sightseeingSupplier.phone)}
                                          </div>
                                          {(sightseeingSupplier.purchase_currency ||
                                            sightseeingSupplier.purchase_price) && (
                                            <div className='small text-muted mt-1'>
                                              <i className='fa fa-money-bill me-1'></i>
                                              {sightseeingSupplier.purchase_currency && (
                                                <span>
                                                  {sightseeingSupplier.purchase_currency}{' '}
                                                </span>
                                              )}
                                              {sightseeingSupplier.purchase_price && (
                                                <span>{sightseeingSupplier.purchase_price}</span>
                                              )}
                                              {sightseeingSupplier.purchase_description && (
                                                <span>
                                                  {' '}
                                                  - {sightseeingSupplier.purchase_description}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          className='btn btn-sm btn-outline-secondary ms-2'
                                          onClick={() => {
                                            setActiveService(sightseeingItem)
                                            setEditingSupplier(sightseeingSupplier)
                                            setShowSightSeeingSupplierModal(true)
                                          }}
                                        >
                                          <i className='fa fa-edit'></i>
                                        </button>
                                        <button
                                          className='btn btn-sm btn-outline-secondary ms-2'
                                          onClick={() =>
                                            removeSightSeeingSupplier(
                                              sightseeingItem.id,
                                              sightseeingSupplier.id
                                            )
                                          }
                                        >
                                          <i className='fa fa-trash text-danger'></i>
                                        </button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <span className='badge badge-light text-dark p-2 border'>
                                  <i className='fa fa-check-circle me-1 text-success'></i>
                                  Confirmation No:{' '}
                                  {sightseeingItem.confirmation_no || 'Not confirmed'}
                                </span>
                                <button
                                  className='btn btn-sm btn-light ms-2'
                                  onClick={() => {
                                    setConfirmationService({
                                      id: sightseeingItem.id,
                                      name: sightseeingItem.sight_seeing?.name,
                                      confirmation_no: sightseeingItem.confirmation_no || '',
                                      service: 'sight-seeing',
                                    })
                                    setShowUpdateConfirmationModal(true)
                                  }}
                                  title='Edit Confirmation Number'
                                >
                                  <i className='fa fa-edit text-primary'></i>
                                </button>
                              </div>
                              <div className='mt-2'>
                                <OnTourStatusSelect
                                  service='sight-seeing'
                                  id={sightseeingItem.id}
                                  status={sightseeingItem.ontour_status}
                                  chargeAmount={sightseeingItem.ontour_charge_amount}
                                  chargeCurrency={sightseeingItem.ontour_charge_currency}
                                  onSaved={refetch}
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  )
                )}
                {booking?.booking_custom_services.map((customItem: any, customIndex: number) => (
                  <Col md={12} key={`custom_${index}_${customIndex}`} className='mb-4'>
                    <Table
                      bordered
                      responsive
                      key={`custom_data_${customIndex}`}
                      className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                    >
                      <thead>
                        <tr className='vertical-align-middle'>
                          <th className='text-capitalize gap-2 align-items-center'>
                            <span className='badge badge-info me-2'>Custom</span>
                            <span className='fw-bold'>{customItem.name}</span>
                            {customItem.transfer_type && (
                              <span className='badge badge-primary ms-2'>
                                {customItem.transfer_type}
                              </span>
                            )}
                            <span
                              className={`badge badge-${getStatusBadgeClass(
                                customItem.booking_status
                              )} ms-2`}
                            >
                              {customItem.booking_status}
                            </span>
                          </th>
                          <th className='text-center'>
                            <i className='fa fa-calendar me-1'></i>
                            {moment(customItem.service_date).format('DD/MM/YYYY')}
                          </th>
                          <th>
                            <div className='d-flex justify-content-between vertical-align-middle'>
                              <Link
                                target='_blank'
                                to={`/bookings/${booking.identifier}/show`}
                                className='btn btn-sm btn-outline-primary'
                              >
                                <i className='fa fa-external-link me-1'></i>
                                {GetPNRNumber({booking: booking})}
                              </Link>
                              <button
                                className='btn btn-sm btn-success'
                                onClick={() => {
                                  if (customItem.custom_service_suppliers.length > 0) {
                                    copyToClipboardAsync(async () => {
                                      const response = await postRequest(
                                        `${process.env.REACT_APP_API_BASE_URL}bookings/process-custom-service`,
                                        {custom_service_id: customItem.id}
                                      )
                                      handleNotificationWithToastify(response.data)
                                      return response.data.data
                                    })
                                  } else {
                                    handleNotificationWithToastify({
                                      message: 'Please select supplier first',
                                      type: 'warning',
                                    })
                                  }
                                }}
                              >
                                <i className='fa fa-check-circle me-1'></i> Process
                              </button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className='text-capitalize w-50'>
                            <i className='fa fa-user me-1 text-primary'></i>
                            {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                          </td>
                          <td>
                            <span className='badge badge-primary me-1'>
                              <i className='fa fa-user me-1'></i>
                              {customItem.adults.length} Adult
                            </span>
                          </td>
                          <td>
                            {customItem.children.filter((child: {age: number}) => child.age > 2)
                              .length > 0 ? (
                              <span className='badge badge-info me-1'>
                                <i className='fa fa-child me-1'></i>
                                {
                                  customItem.children.filter(
                                    (child: {age: number}) => child.age > 2
                                  ).length
                                }{' '}
                                Child
                              </span>
                            ) : (
                              ''
                            )}
                            {customItem.children.filter((child: {age: number}) => child.age <= 2)
                              .length > 0 ? (
                              <span className='badge badge-secondary'>
                                <i className='fa fa-baby me-1'></i>
                                {
                                  customItem.children.filter(
                                    (child: {age: number}) => child.age <= 2
                                  ).length
                                }{' '}
                                Infant
                              </span>
                            ) : (
                              ''
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <div className='mb-2'>
                              <span className='fw-bold me-1'>Pickup:</span>
                              <i className='fa fa-map-marker me-1 text-danger'></i>
                              {customItem.pickup_from}
                            </div>
                            <div>
                              <span className='fw-bold me-1'>Drop:</span>
                              <i className='fa fa-map-marker me-1 text-success'></i>
                              {customItem.drop_to}
                            </div>
                          </td>
                          <td>
                            <span className='badge badge-primary'>
                              <i className='fa fa-car me-1'></i>
                              {customItem.vehicle_type}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <i className='fa fa-clock-o me-1 text-warning'></i>
                            Pickup: <LongTimeFormat time={customItem?.pickup_time} />
                          </td>
                          <td colSpan={2}>
                            <i className='fa fa-clock-o me-1 text-success'></i>
                            Start:
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3}>
                            <button
                              className='btn btn-link p-0 text-decoration-none'
                              onClick={() => {
                                setActiveService(customItem)
                                setShowCustomServiceDateChangeModal(true)
                              }}
                            >
                              <i className='fa fa-edit me-1'></i> Remarks:
                            </button>
                            <span className='ms-2'>
                              {customItem.special_request || 'No remarks'}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <div className='d-flex justify-content-between flex-start'>
                              <div>
                                <button
                                  className='btn btn-link p-0 text-decoration-none mb-2'
                                  onClick={() => {
                                    setActiveService(customItem)
                                    setEditingSupplier(undefined)
                                    setShowCustomServiceSupplierModal(true)
                                  }}
                                >
                                  <i className='fa fa-plus-circle me-1'></i> Supplier:
                                </button>
                                {customItem.custom_service_suppliers.map(
                                  (customServiceSupplier: any, index: number) => (
                                    <div
                                      key={`custom_service_supplier_${index}_${customServiceSupplier.id}`}
                                      className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                                    >
                                      <div className='flex-grow-1'>
                                        <div className='fw-bold'>
                                          {customServiceSupplier.company_name}
                                        </div>
                                        <div className='small'>
                                          <i className='fa fa-user me-1'></i>{' '}
                                          {customServiceSupplier.contact_person}
                                          <i className='fa fa-phone ms-2 me-1'></i>{' '}
                                          {formatPhone(customServiceSupplier.phone)}
                                        </div>
                                        {(customServiceSupplier.purchase_currency ||
                                          customServiceSupplier.purchase_price) && (
                                          <div className='small text-muted mt-1'>
                                            <i className='fa fa-money-bill me-1'></i>
                                            {customServiceSupplier.purchase_currency && (
                                              <span>
                                                {customServiceSupplier.purchase_currency}{' '}
                                              </span>
                                            )}
                                            {customServiceSupplier.purchase_price && (
                                              <span>{customServiceSupplier.purchase_price}</span>
                                            )}
                                            {customServiceSupplier.purchase_description && (
                                              <span>
                                                {' '}
                                                - {customServiceSupplier.purchase_description}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        className='btn btn-sm btn-outline-secondary ms-2'
                                        onClick={() => {
                                          setActiveService(customItem)
                                          setEditingSupplier(customServiceSupplier)
                                          setShowCustomServiceSupplierModal(true)
                                        }}
                                      >
                                        <i className='fa fa-edit'></i>
                                      </button>
                                      <button
                                        className='btn btn-sm btn-outline-secondary ms-2'
                                        onClick={() =>
                                          removeCustomServiceSupplier(
                                            customItem.id,
                                            customServiceSupplier.id
                                          )
                                        }
                                      >
                                        <i className='fa fa-trash text-danger'></i>
                                      </button>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              <span className='badge badge-light text-dark p-2 border'>
                                <i className='fa fa-check-circle me-1 text-success'></i>
                                Confirmation No: {customItem.confirmation_no || 'Not confirmed'}
                              </span>
                              <button
                                className='btn btn-sm btn-light ms-2'
                                onClick={() => {
                                  setConfirmationService({
                                    id: customItem.id,
                                    name: customItem.name,
                                    confirmation_no: customItem.confirmation_no || '',
                                    service: 'custom',
                                  })
                                  setShowUpdateConfirmationModal(true)
                                }}
                                title='Edit Confirmation Number'
                              >
                                <i className='fa fa-edit text-primary'></i>
                              </button>
                            </div>
                            <div className='mt-2'>
                              <OnTourStatusSelect
                                service='custom'
                                id={customItem.id}
                                status={customItem.ontour_status}
                                chargeAmount={customItem.ontour_charge_amount}
                                chargeCurrency={customItem.ontour_charge_currency}
                                onSaved={refetch}
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                ))}
                {booking?.booking_packages?.map((packageItem: any, packageIndex: number) =>
                  (packageItem.itineraries || []).map((itineraryItem: any) => (
                    <Col
                      md={12}
                      key={`package_${index}_${packageIndex}_${itineraryItem.id}`}
                      className='mb-4'
                    >
                      <Table
                        bordered
                        responsive
                        className='vertical-align-middle table-striped font-weight-bold custom-table-border custom-table-padding'
                      >
                        <thead>
                          <tr className='vertical-align-middle'>
                            <th className='text-capitalize gap-2 align-items-center'>
                              <span className='badge badge-warning me-2'>Package</span>
                              <span className='fw-bold'>{packageItem.package?.package_name}</span>
                              <span className='badge badge-primary ms-2'>
                                Day {itineraryItem.day} — {itineraryItem.service}
                              </span>
                              {itineraryItem.type && (
                                <span className='badge badge-info ms-2'>{itineraryItem.type}</span>
                              )}
                              <span
                                className={`badge badge-${getStatusBadgeClass(
                                  itineraryStatus(packageItem, itineraryItem)
                                )} ms-2`}
                              >
                                {itineraryStatus(packageItem, itineraryItem)}
                              </span>
                            </th>
                            <th className='text-center'>
                              <i className='fa fa-calendar me-1'></i>
                              {moment(itineraryItem.date).format('DD/MM/YYYY')}
                            </th>
                            <th>
                              <div className='d-flex justify-content-between vertical-align-middle'>
                                <Link
                                  target='_blank'
                                  to={`/bookings/${booking.identifier}/show`}
                                  className='btn btn-sm btn-outline-primary'
                                >
                                  <i className='fa fa-external-link me-1'></i>
                                  {GetPNRNumber({booking: booking})}
                                </Link>
                                <button
                                  className='btn btn-sm btn-success'
                                  onClick={() => {
                                    if ((itineraryItem.itinerary_suppliers || []).length > 0) {
                                      copyToClipboardAsync(async () => {
                                        const response = await postRequest(
                                          `${process.env.REACT_APP_API_BASE_URL}bookings/process-package-itinerary`,
                                          {itinerary_id: itineraryItem.id}
                                        )
                                        handleNotificationWithToastify(response.data)
                                        return response.data.data
                                      })
                                    } else {
                                      handleNotificationWithToastify({
                                        message: 'Please select supplier first',
                                        type: 'warning',
                                      })
                                    }
                                  }}
                                >
                                  <i className='fa fa-check-circle me-1'></i> Process
                                </button>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className='text-capitalize w-50'>
                              <i className='fa fa-user me-1 text-primary'></i>
                              {`${booking.lead_passenger.prefix}. ${booking.lead_passenger.first_name} ${booking.lead_passenger.last_name}`}
                            </td>
                            <td>
                              <span className='badge badge-primary me-1'>
                                <i className='fa fa-user me-1'></i>
                                {(packageItem.adults || []).length} Adult
                              </span>
                            </td>
                            <td>
                              {(packageItem.children || []).filter(
                                (child: {age: number}) => child.age > 2
                              ).length > 0 && (
                                <span className='badge badge-info me-1'>
                                  <i className='fa fa-child me-1'></i>
                                  {
                                    (packageItem.children || []).filter(
                                      (child: {age: number}) => child.age > 2
                                    ).length
                                  }{' '}
                                  Child
                                </span>
                              )}
                              {(packageItem.children || []).filter(
                                (child: {age: number}) => child.age <= 2
                              ).length > 0 && (
                                <span className='badge badge-secondary'>
                                  <i className='fa fa-baby me-1'></i>
                                  {
                                    (packageItem.children || []).filter(
                                      (child: {age: number}) => child.age <= 2
                                    ).length
                                  }{' '}
                                  Infant
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <div className='mb-2'>
                                <span className='fw-bold me-1'>Pickup:</span>
                                <i className='fa fa-map-marker me-1 text-danger'></i>
                                {itineraryItem.city_from?.name || '-'}
                              </div>
                              <div className='mb-2'>
                                <span className='fw-bold me-1'>Drop:</span>
                                <i className='fa fa-map-marker me-1 text-success'></i>
                                {itineraryItem.city_to?.name || '-'}
                              </div>
                              {(itineraryItem.service_type === 'Arrival' ||
                                itineraryItem.service_type === 'Departure') && (
                                <div>
                                  <i className='fa fa-plane me-1 text-primary'></i>
                                  {itineraryItem.service_type} Flight No.{' '}
                                  {itineraryItem.flight_no || '-'} — {itineraryItem.estimated_time}
                                </div>
                              )}
                            </td>
                            <td>
                              {itineraryItem.vehicle_type && (
                                <span className='badge badge-primary'>
                                  <i className='fa fa-car me-1'></i>
                                  {itineraryItem.vehicle_type}
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3}>
                              <i className='fa fa-clock-o me-1 text-warning'></i>
                              Pickup: <LongTimeFormat time={itineraryItem.pickup_time} />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3}>
                              <button
                                className='btn btn-link p-0 text-decoration-none'
                                onClick={() => {
                                  setActiveService(itineraryItem)
                                  setShowPackageItineraryRemarkModal(true)
                                }}
                              >
                                <i className='fa fa-edit me-1'></i> Remarks:
                              </button>
                              <span className='ms-2'>{itineraryItem.remark || 'No remarks'}</span>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={2}>
                              <div className='d-flex justify-content-between flex-start'>
                                <div>
                                  <button
                                    className='btn btn-link p-0 text-decoration-none mb-2'
                                    onClick={() => {
                                      setActiveService(itineraryItem)
                                      setEditingSupplier(undefined)
                                      setShowPackageItinerarySupplierModal(true)
                                    }}
                                  >
                                    <i className='fa fa-plus-circle me-1'></i> Supplier:
                                  </button>
                                  {(itineraryItem.itinerary_suppliers || []).map(
                                    (itinerarySupplier: any, supplierIndex: number) => (
                                      <div
                                        key={`itinerary_supplier_${supplierIndex}_${itinerarySupplier.id}`}
                                        className='d-flex align-items-center mb-2 p-2 bg-light rounded'
                                      >
                                        <div className='flex-grow-1'>
                                          <div className='fw-bold'>
                                            {itinerarySupplier.company_name}
                                          </div>
                                          <div className='small'>
                                            <i className='fa fa-user me-1'></i>{' '}
                                            {itinerarySupplier.contact_person}
                                            <i className='fa fa-phone ms-2 me-1'></i>{' '}
                                            {formatPhone(itinerarySupplier.phone)}
                                          </div>
                                          {(itinerarySupplier.purchase_currency ||
                                            itinerarySupplier.purchase_price) && (
                                            <div className='small text-muted mt-1'>
                                              <i className='fa fa-money-bill me-1'></i>
                                              {itinerarySupplier.purchase_currency && (
                                                <span>{itinerarySupplier.purchase_currency} </span>
                                              )}
                                              {itinerarySupplier.purchase_price && (
                                                <span>{itinerarySupplier.purchase_price}</span>
                                              )}
                                              {itinerarySupplier.purchase_description && (
                                                <span>
                                                  {' '}
                                                  - {itinerarySupplier.purchase_description}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <button
                                          className='btn btn-sm btn-outline-secondary ms-2'
                                          onClick={() => {
                                            setActiveService(itineraryItem)
                                            setEditingSupplier(itinerarySupplier)
                                            setShowPackageItinerarySupplierModal(true)
                                          }}
                                        >
                                          <i className='fa fa-edit'></i>
                                        </button>
                                        <button
                                          className='btn btn-sm btn-outline-secondary ms-2'
                                          onClick={() =>
                                            removePackageItinerarySupplier(
                                              itineraryItem.id,
                                              itinerarySupplier.id
                                            )
                                          }
                                        >
                                          <i className='fa fa-trash text-danger'></i>
                                        </button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <span className='badge badge-light text-dark p-2 border'>
                                  <i className='fa fa-check-circle me-1 text-success'></i>
                                  Confirmation No:{' '}
                                  {itineraryItem.confirmation_no || 'Not confirmed'}
                                </span>
                                <button
                                  className='btn btn-sm btn-light ms-2'
                                  onClick={() => {
                                    setConfirmationService({
                                      id: itineraryItem.id,
                                      name: `Day ${itineraryItem.day} - ${itineraryItem.service}`,
                                      confirmation_no: itineraryItem.confirmation_no || '',
                                      service: 'package_itinerary',
                                    })
                                    setShowUpdateConfirmationModal(true)
                                  }}
                                  title='Edit Confirmation Number'
                                >
                                  <i className='fa fa-edit text-primary'></i>
                                </button>
                              </div>
                              <div className='mt-2'>
                                <OnTourStatusSelect
                                  service='package_itinerary'
                                  id={itineraryItem.id}
                                  status={itineraryItem.ontour_status}
                                  chargeAmount={itineraryItem.ontour_charge_amount}
                                  chargeCurrency={itineraryItem.ontour_charge_currency}
                                  onSaved={refetch}
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  ))
                )}
              </React.Fragment>
            ))}
          </Row>
          <Row>
            <Col>
              <div className='d-flex justify-content-center mt-4'>
                <div className='p-2 rounded shadow-sm'>
                  <Pagination
                    count={Number(response?.data?.count ?? 1)}
                    page={currentPage}
                    siblingCount={2}
                    onChange={handlePaginationChange}
                    shape='rounded'
                    color='primary'
                  />
                </div>
              </div>
            </Col>
          </Row>
        </KTCardBody>
      </KTCard>
      {showSightSeeingSupplierModal && (
        <ManageSightSeenSupplier
          show={showSightSeeingSupplierModal}
          hide={() => setShowSightSeeingSupplierModal(false)}
          sightSeen={activeService}
          supplier={editingSupplier}
          refetch={refetch}
        />
      )}
      {showTransferSupplierModal && (
        <ManageTransferSupplier
          show={showTransferSupplierModal}
          hide={() => setShowTransferSupplierModal(false)}
          transfer={activeService}
          supplier={editingSupplier}
          refetch={refetch}
        />
      )}
      {showCustomServiceSupplierModal && (
        <ManageCustomServiceSupplier
          show={showCustomServiceSupplierModal}
          hide={() => setShowCustomServiceSupplierModal(false)}
          customService={activeService}
          supplier={editingSupplier}
          refetch={refetch}
        />
      )}
      {showTransferDateChangeModal && (
        <ManageTransferRemark
          show={showTransferDateChangeModal}
          hide={() => setShowTransferDateChangeModal(false)}
          transfer={activeService}
          refetch={refetch}
        />
      )}
      {showSightSeeingDateChangeModal && (
        <ManageSightSeenRemark
          show={showSightSeeingDateChangeModal}
          hide={() => setShowSightSeeingDateChangeModal(false)}
          sightSeen={activeService}
          refetch={refetch}
        />
      )}
      {showCustomServiceDateChangeModal && (
        <ManageCustomServiceRemark
          show={showCustomServiceDateChangeModal}
          hide={() => setShowCustomServiceDateChangeModal(false)}
          customService={activeService}
          refetch={refetch}
        />
      )}
      {showPackageItinerarySupplierModal && (
        <ManagePackageItinerarySupplier
          show={showPackageItinerarySupplierModal}
          hide={() => setShowPackageItinerarySupplierModal(false)}
          itinerary={activeService}
          supplier={editingSupplier}
          refetch={refetch}
        />
      )}
      {showPackageItineraryRemarkModal && (
        <ManagePackageItineraryRemark
          show={showPackageItineraryRemarkModal}
          hide={() => setShowPackageItineraryRemarkModal(false)}
          itinerary={activeService}
          refetch={refetch}
        />
      )}
      {showUpdateConfirmationModal && confirmationService && (
        <UpdateConfirmationNumber
          show={showUpdateConfirmationModal}
          hide={() => {
            setShowUpdateConfirmationModal(false)
            setConfirmationService(null)
            refetch()
          }}
          id={confirmationService.id}
          name={confirmationService.name}
          confirmation_no={confirmationService.confirmation_no}
          service={confirmationService.service}
        />
      )}
    </>
  )
}
